import { collection } from '@/infra/collection'
import {
  Notification,
  type NotificationProps,
} from '@/features/activity/Notification'
import { type NotificationApiRoot } from '@/features/activity/api/notification-api'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import type {
  GetCurrentMemberFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import {
  type NotificationRealtimeEventResource,
  type NotificationResource,
} from '@/features/activity/api/resources/notification'
import { safeParseDate } from '@/utils/date-util'
import { Id } from '@/infra/id'
import { filter, map } from 'rxjs'
import { share } from 'rxjs/operators'
import { computed } from 'mobx'

/**
 * Store for managing notifications.
 */
export class NotificationStore {
  readonly notifications = collection({
    create: () => new Notification(),
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
  })

  /**
   * Emits new notifications.
   */
  readonly newNotifications$

  /**
   * Emits notifications that are removed from memory.
   */
  readonly notificationRemoved$ = this.notifications.removals$

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _api: NotificationApiRoot,
  ) {
    this.newNotifications$ = this._api.notifications.events$.pipe(
      map((evt) => this._handleRealtimeEvent(evt)),
      filter((n): n is Notification => n?.status === 'Sent'),
      share(),
    )
    this.newNotifications$.subscribe()
  }

  /**
   * Gets the unread notifications count for the current workspace.
   */
  @computed
  get unreadNotificationsCount() {
    const workspace = this._getCurrentMember().workspace
    return this.notifications.filter(
      (n) => n.workspace === workspace && n.status !== 'Read',
    ).length
  }

  /**
   * Lists notifications for the current user and workspace.
   *
   * @param paging
   * @param signal
   */
  async listNotifications(
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Notification>> {
    const currentMember = this._getCurrentMember()
    const response = await this._api.notifications.list(
      {
        after: paging.after,
        limit: paging.limit,
        workspace_id: currentMember.workspaceId,
      },
      signal,
    )

    return {
      next: response.cursor,
      total: response.total,
      data: this.notifications.set(
        response.data.map((r) => this._mapNotificationResourceToProps(r)),
      ),
    }
  }

  /**
   * Marks the given notifications as read.
   * @param notifications
   * @param signal
   */
  async markRead(notifications: Notification[], signal?: AbortSignal) {
    await this._api.notifications.batchChangeStatus(
      notifications.map((n) => ({
        id: Id.assert(n.id),
        status: 'Read',
      })),
      signal,
    )
  }

  private _mapNotificationResourceToProps(
    resource: NotificationResource,
  ): NotificationProps {
    let image: NotificationProps['image'] = { type: 'None' }
    switch (resource.image.type) {
      case 'Static':
        image = { type: 'Static', url: resource.image.url }
        break
      case 'UserAvatar':
        image = {
          type: 'UserAvatar',
          user: this._primeUser({ id: resource.image.user_public_id }),
        }
        break
    }

    return {
      id: resource.id,
      action:
        resource.action.type === 'ViewJob'
          ? { type: 'ViewJob', jobId: resource.action.job_id }
          : { type: 'None' },
      image,
      dispatchedAt: safeParseDate(resource.date_dispatch),
      message: resource.message,
      workspace: this._primeWorkspace({ id: resource.workspace_id }),
      status: resource.status,
    }
  }

  /**
   * Handles an incoming notification update from the server.
   * @param evt
   * @private
   */
  private _handleRealtimeEvent(
    evt: NotificationRealtimeEventResource,
  ): Notification | null {
    if (evt.type !== 'NotificationUpdated') {
      return null
    }

    // Filter out notifications for non-current workspace.
    const currentWorkspaceId = this._getCurrentMember().workspaceId
    if (evt.data.workspace_id !== currentWorkspaceId) {
      return null
    }

    const props = this._mapNotificationResourceToProps(evt.data)
    return this.notifications.set(props)
  }
}
